import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, ButtonGroup, Button, Table, Badge } from 'react-bootstrap';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  FaUsers, FaChartLine, FaDownload, FaSignInAlt, FaTrophy, FaClock, FaLayerGroup, FaStar, FaChartPie
} from 'react-icons/fa';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAdminGuard } from '../../../hooks/useAdminGuard';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const ActivityDashboard = () => {
  useAdminGuard(3); // Requiere AdminLevel 3

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7');
  const [dashboardData, setDashboardData] = useState(null);

  const fetchDashboardData = async (days = 7) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/activity-logs/dashboard?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Dashboard API Response:', data);
      setDashboardData(data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Error al cargar el dashboard de actividades');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(timeRange);
  }, [timeRange]);

  const COLORS = ['#667eea', '#00D9FF', '#FEB019', '#00E396', '#FF4560'];

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando dashboard...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const totalAcciones = dashboardData?.userMetrics?.total_acciones || 0;
  const usuariosActivos = dashboardData?.userMetrics?.usuarios_unicos || 0;
  const promedioAcciones = dashboardData?.userMetrics?.promedio_acciones_por_usuario || 0;

  // Preparar datos de horarios
  const horariosData = dashboardData?.activityByHour?.map(h => ({
    hora: `${h.hora}:00`,
    total: h.total
  })) || [];

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col md={8}>
          <h2 className="mb-1">
            <FaChartLine className="me-2" style={{ color: '#667eea' }} />
            Dashboard de Actividades
          </h2>
          <p className="text-muted">Monitoreo y análisis de actividad del sistema</p>
        </Col>
        <Col md={4} className="d-flex justify-content-end align-items-center">
          <ButtonGroup>
            <Button 
              size="sm"
              variant={timeRange === '7' ? 'primary' : 'outline-primary'}
              onClick={() => setTimeRange('7')}
            >
              7 días
            </Button>
            <Button 
              size="sm"
              variant={timeRange === '15' ? 'primary' : 'outline-primary'}
              onClick={() => setTimeRange('15')}
            >
              15 días
            </Button>
            <Button 
              size="sm"
              variant={timeRange === '30' ? 'primary' : 'outline-primary'}
              onClick={() => setTimeRange('30')}
            >
              30 días
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="h-100 shadow-sm" style={{ borderLeft: '4px solid #667eea' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1">Total Acciones</h6>
                  <h3 className="mb-0" style={{ color: '#667eea' }}>{totalAcciones.toLocaleString()}</h3>
                  <small className="text-muted">últimos {timeRange} días</small>
                </div>
                <div style={{ color: '#667eea' }}>
                  <FaChartLine size={30} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="h-100 shadow-sm" style={{ borderLeft: '4px solid #00D9FF' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1">Usuarios Únicos</h6>
                  <h3 className="mb-0" style={{ color: '#00D9FF' }}>{usuariosActivos}</h3>
                  <small className="text-muted">en período</small>
                </div>
                <div style={{ color: '#00D9FF' }}>
                  <FaUsers size={30} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="h-100 shadow-sm" style={{ borderLeft: '4px solid #FEB019' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1">Promedio Acciones</h6>
                  <h3 className="mb-0" style={{ color: '#FEB019' }}>{promedioAcciones}</h3>
                  <small className="text-muted">por usuario</small>
                </div>
                <div style={{ color: '#FEB019' }}>
                  <FaChartPie size={30} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="h-100 shadow-sm" style={{ borderLeft: '4px solid #00E396' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1">Hora Pico</h6>
                  <h3 className="mb-0" style={{ color: '#00E396' }}>
                    {horariosData.reduce((max, h) => h.total > max.total ? h : max, {hora: '-', total: 0}).hora}
                  </h3>
                  <small className="text-muted">mayor actividad</small>
                </div>
                <div style={{ color: '#00E396' }}>
                  <FaClock size={30} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráficos principales */}
      <Row className="mb-4">
        {/* Actividad diaria */}
        <Col lg={8}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaClock className="me-2 text-primary" />
                Tendencia de Actividad
              </h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboardData?.activityByDay || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="fecha" 
                    tickFormatter={(date) => format(new Date(date), 'dd/MM', { locale: es })}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(date) => format(new Date(date), 'dd MMMM yyyy', { locale: es })}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#667eea" 
                    strokeWidth={2}
                    name="Actividades"
                    dot={{ fill: '#667eea', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Categorías de acciones */}
        <Col lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaLayerGroup className="me-2 text-primary" />
                Por Categoría
              </h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData?.actionCategories || []}
                    dataKey="total"
                    nameKey="categoria"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry) => `${entry.total}`}
                  >
                    {(dashboardData?.actionCategories || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actividad por hora */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaClock className="me-2 text-primary" />
                Actividad por Hora del Día
              </h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={horariosData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hora" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="total" fill="#00D9FF" name="Acciones" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Usuarios más activos y Top acciones */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaTrophy className="me-2 text-warning" />
                Top 10 Usuarios Más Activos
              </h5>
            </Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={dashboardData?.topUsuarios || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="nombre" type="category" width={120} />
                  <Tooltip />
                  <Bar dataKey="total_acciones" fill="#667eea" name="Acciones" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaStar className="me-2 text-warning" />
                Top 10 Acciones Más Frecuentes
              </h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Acción</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData?.topAcciones || []).map((accion, idx) => (
                    <tr key={idx}>
                      <td><Badge bg="primary">{idx + 1}</Badge></td>
                      <td>{accion.accion}</td>
                      <td className="text-end"><strong>{accion.total}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Usuarios con más logins y patrones */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaSignInAlt className="me-2 text-success" />
                Top 5 Usuarios con Más Logins
              </h5>
            </Card.Header>
            <Card.Body>
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Usuario</th>
                    <th className="text-center">Tipo</th>
                    <th className="text-end">Logins</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData?.topLogins || []).map((user, idx) => (
                    <tr key={idx}>
                      <td><Badge bg="success">{idx + 1}</Badge></td>
                      <td>{user.nombre || `Legajo ${user.user_legajo}`}</td>
                      <td className="text-center">
                        <Badge bg={user.tipo_usuario === 'Admin' ? 'danger' : 'info'}>
                          {user.tipo_usuario}
                        </Badge>
                      </td>
                      <td className="text-end"><strong>{user.total_logins}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaChartLine className="me-2 text-info" />
                Patrones de Uso - Top Usuarios
              </h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Acción Frecuente</th>
                    <th className="text-end">Veces</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData?.userActionPatterns || []).map((pattern, idx) => (
                    <tr key={idx}>
                      <td><small>{pattern.nombre || '-'}</small></td>
                      <td><small>{pattern.accion}</small></td>
                      <td className="text-end"><Badge bg="info">{pattern.veces}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Últimas Actividades Detalladas */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaClock className="me-2 text-primary" />
                Últimas 50 Actividades
              </h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table hover size="sm" responsive>
                <thead className="sticky-top bg-white">
                  <tr>
                    <th style={{ width: '140px' }}>Fecha/Hora</th>
                    <th style={{ width: '120px' }}>Usuario</th>
                    <th style={{ width: '80px' }}>Tipo</th>
                    <th>Acción</th>
                    <th style={{ width: '100px' }}>Entidad</th>
                    <th style={{ width: '100px' }}>ID</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData?.ultimasActividades || []).map((log, idx) => (
                    <tr key={idx}>
                      <td>
                        <small className="text-muted">
                          {format(new Date(log.created_at), 'dd/MM/yy HH:mm', { locale: es })}
                        </small>
                      </td>
                      <td>
                        <small><strong>{log.usuario_nombre || `Legajo ${log.user_legajo}`}</strong></small>
                      </td>
                      <td>
                        <Badge 
                          bg={log.usuario_tipo === 'Admin' ? 'danger' : 'info'} 
                          pill 
                          className="text-white"
                        >
                          {log.usuario_tipo}
                        </Badge>
                      </td>
                      <td>
                        <small>{log.accion}</small>
                      </td>
                      <td>
                        <small className="text-muted">{log.entity_type || '-'}</small>
                      </td>
                      <td>
                        <small className="text-muted">{log.entity_id || '-'}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {(!dashboardData?.ultimasActividades || dashboardData.ultimasActividades.length === 0) && (
                <div className="text-center text-muted py-4">
                  <p>No hay actividades registradas en este período</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ActivityDashboard;
